/**
 * useDocumentsList Hook
 *
 * Business logic for documents list view including:
 * - Fetching documents with server-side pagination
 * - Search functionality with debouncing
 * - Filtering by document type, patient, and date range
 * - Sorting
 * - URL state synchronization
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { useGetDocumentsQuery } from '../store/api';
import type { DocumentSummaryResponse } from '../types';

export type SortField = 'created_at' | 'updated_at' | 'filename' | 'size_bytes';
export type SortOrder = 'asc' | 'desc';

export interface DocumentFiltersState {
  search: string;
  documentType: string | null;
  patientId: string | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export interface UseDocumentsListOptions {
  initialPageSize?: number;
  syncWithUrl?: boolean;
  /** Fixed document type filter - cannot be changed by user */
  fixedDocumentType?: string;
}

export interface UseDocumentsListReturn {
  // Data
  documents: DocumentSummaryResponse[];
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;

  // Pagination
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Filters
  filters: DocumentFiltersState;
  sortBy: SortField;
  sortOrder: SortOrder;

  // State helpers
  isEmpty: boolean;
  isSearchActive: boolean;
  hasActiveFilters: boolean;

  // Actions
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (term: string) => void;
  setDocumentType: (type: string | null) => void;
  setPatientId: (patientId: string | null) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;
  setSortBy: (field: SortField) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleSort: (field: SortField) => void;
  clearFilters: () => void;
  refresh: () => void;
}

const DEFAULT_PAGE_SIZE = 20;

export function useDocumentsList(
  options: UseDocumentsListOptions = {},
): UseDocumentsListReturn {
  const { initialPageSize = DEFAULT_PAGE_SIZE, syncWithUrl = true, fixedDocumentType } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params if syncWithUrl is enabled
  const getInitialValue = <T>(key: string, defaultValue: T, parser?: (val: string) => T): T => {
    if (!syncWithUrl) return defaultValue;
    const param = searchParams.get(key);
    if (!param) return defaultValue;
    return parser ? parser(param) : (param as unknown as T);
  };

  // Pagination state
  const [page, setPageState] = useState(() => getInitialValue('page', 1, Number));
  const [pageSize, setPageSizeState] = useState(() =>
    getInitialValue('perPage', initialPageSize, Number),
  );

  // Filter state
  const [search, setSearchState] = useState(() => getInitialValue('search', ''));
  const [documentType, setDocumentTypeState] = useState<string | null>(() =>
    getInitialValue('documentType', null),
  );
  const [patientId, setPatientIdState] = useState<string | null>(() =>
    getInitialValue('patientId', null),
  );
  const [startDate, setStartDateState] = useState<Dayjs | null>(() =>
    getInitialValue('startDate', null, val => dayjs(val)),
  );
  const [endDate, setEndDateState] = useState<Dayjs | null>(() =>
    getInitialValue('endDate', null, val => dayjs(val)),
  );

  // Sort state
  const [sortBy, setSortByState] = useState<SortField>(() =>
    getInitialValue('sortBy', 'created_at' as SortField),
  );
  const [sortOrder, setSortOrderState] = useState<SortOrder>(() =>
    getInitialValue('sortOrder', 'desc' as SortOrder),
  );

  // Debounce search for API calls
  const debouncedSearch = useDebounce(search, 300);

  // Sync state to URL when values change
  useEffect(() => {
    if (!syncWithUrl) return;

    const newParams = new URLSearchParams();

    if (page !== 1) newParams.set('page', String(page));
    if (pageSize !== DEFAULT_PAGE_SIZE) newParams.set('perPage', String(pageSize));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (documentType) newParams.set('documentType', documentType);
    if (patientId) newParams.set('patientId', patientId);
    if (startDate) newParams.set('startDate', startDate.toISOString());
    if (endDate) newParams.set('endDate', endDate.toISOString());
    if (sortBy !== 'created_at') newParams.set('sortBy', sortBy);
    if (sortOrder !== 'desc') newParams.set('sortOrder', sortOrder);

    setSearchParams(newParams, { replace: true });
  }, [
    page,
    pageSize,
    debouncedSearch,
    documentType,
    patientId,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    syncWithUrl,
    setSearchParams,
  ]);

  // Use fixed document type if provided, otherwise use user-selected
  const effectiveDocumentType = fixedDocumentType || documentType;

  // Build API query args
  const queryArgs = useMemo(
    () => ({
      page,
      perPage: pageSize,
      search: debouncedSearch || undefined,
      documentType: effectiveDocumentType || undefined,
      patientId: patientId || undefined,
      startDate: startDate?.toISOString() || undefined,
      endDate: endDate?.toISOString() || undefined,
      sortBy,
      sortOrder,
    }),
    [page, pageSize, debouncedSearch, effectiveDocumentType, patientId, startDate, endDate, sortBy, sortOrder],
  );

  // RTK Query hook
  const { data, isLoading, isFetching, error, refetch } = useGetDocumentsQuery(queryArgs);

  // Derived values
  const documents = data?.items ?? [];
  const totalRows = data?.total ?? 0;
  const totalPages = data?.total_pages ?? Math.ceil(totalRows / pageSize);

  const filters: DocumentFiltersState = useMemo(
    () => ({
      search,
      documentType: effectiveDocumentType,
      patientId,
      startDate,
      endDate,
    }),
    [search, effectiveDocumentType, patientId, startDate, endDate],
  );

  // Don't count fixedDocumentType as an "active filter" since user can't change it
  const hasActiveFilters = useMemo(
    () =>
      Boolean(search || (!fixedDocumentType && documentType) || patientId || startDate || endDate),
    [search, fixedDocumentType, documentType, patientId, startDate, endDate],
  );

  // Actions
  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPageState(1); // Reset to first page when changing page size
  }, []);

  const setSearch = useCallback((term: string) => {
    setSearchState(term);
    setPageState(1); // Reset to first page on search
  }, []);

  const setDocumentType = useCallback((type: string | null) => {
    setDocumentTypeState(type);
    setPageState(1);
  }, []);

  const setPatientId = useCallback((id: string | null) => {
    setPatientIdState(id);
    setPageState(1);
  }, []);

  const setStartDate = useCallback((date: Dayjs | null) => {
    setStartDateState(date);
    setPageState(1);
  }, []);

  const setEndDate = useCallback((date: Dayjs | null) => {
    setEndDateState(date);
    setPageState(1);
  }, []);

  const setSortBy = useCallback((field: SortField) => {
    setSortByState(field);
  }, []);

  const setSortOrder = useCallback((order: SortOrder) => {
    setSortOrderState(order);
  }, []);

  const toggleSort = useCallback(
    (field: SortField) => {
      if (sortBy === field) {
        setSortOrderState(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortByState(field);
        setSortOrderState('asc');
      }
    },
    [sortBy, sortOrder],
  );

  const clearFilters = useCallback(() => {
    setSearchState('');
    setDocumentTypeState(null);
    setPatientIdState(null);
    setStartDateState(null);
    setEndDateState(null);
    setPageState(1);
  }, []);

  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    // Data
    documents,
    isLoading,
    isFetching,
    error: error ? 'Failed to load documents. Please try again.' : null,

    // Pagination
    page,
    pageSize,
    totalRows,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,

    // Filters
    filters,
    sortBy,
    sortOrder,

    // State helpers
    isEmpty: !isLoading && documents.length === 0,
    isSearchActive: search.length > 0,
    hasActiveFilters,

    // Actions
    setPage,
    setPageSize,
    setSearch,
    setDocumentType,
    setPatientId,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    toggleSort,
    clearFilters,
    refresh,
  };
}

